import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Edit,
  Close,
  DeleteOutline,
  Description,
  AttachFile,
  Download,
  InsertDriveFile,
  OndemandVideo,
  Visibility,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import FileDropzone from "../components/FileDropzone";
import AddButton from "../components/AddButton";
import ResultCount from "../components/ResultCount";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import SavingOverlay from "../components/SavingOverlay";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography, pageHeader } from "../theme/designSystem";
import { workflowErrorMessage } from "../services/workflowsService";
import {
  playbookDocumentsService,
  documentLibraryService,
  downloadBlob,
  formatFileSize,
  PlaybookDocumentAttachment,
  PlaybookDocumentListItem,
  PlaybookDocumentProfileOption,
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_COUNT,
  CATEGORY_SUGGESTIONS,
} from "../services/playbookDocumentsService";

interface FormState {
  id: string | null; // null = criação
  title: string;
  summary: string;
  category: string;
  groupName: string;
  videoUrl: string;
  visibilityProfileIds: string[];
}

const blankForm = (): FormState => ({
  id: null,
  title: "",
  summary: "",
  category: "",
  groupName: "",
  videoUrl: "",
  visibilityProfileIds: [],
});

// Ação destrutiva confirmada no ConfirmModal: guardar a execução no estado evita um modal por ação.
interface PendingAction {
  title: string;
  message: string;
  confirmText: string;
  run: () => Promise<void>;
}

const PlaybookDocuments: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const entityId = user?.entityId || "";
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const [documents, setDocuments] = useState<PlaybookDocumentListItem[]>([]);
  const [profiles, setProfiles] = useState<PlaybookDocumentProfileOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [attachments, setAttachments] = useState<PlaybookDocumentAttachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"active" | "inactive" | "all">("active");

  const [form, setForm] = useState<FormState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PlaybookDocumentListItem | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const load = async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [items, profileOptions] = await Promise.all([
        playbookDocumentsService.getByEntity(entityId, true),
        playbookDocumentsService.getProfiles().catch(() => [] as PlaybookDocumentProfileOption[]),
      ]);
      setDocuments(items);
      setProfiles(profileOptions);
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao carregar os documentos."), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Configurar biblioteca";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const categories = useMemo(
    () =>
      Array.from(
        new Set(documents.map((d) => (d.category || "").trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, "pt-BR")),
    [documents]
  );

  /** Grupos já usados pela entidade — viram sugestão no cadastro e opção no filtro. */
  const groupOptions = useMemo(
    () =>
      Array.from(
        new Set(documents.map((d) => (d.groupName || "").trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, "pt-BR")),
    [documents]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return documents
      .filter((d) => !term || d.title.toLowerCase().includes(term))
      .filter((d) => categoryFilter === "all" || (d.category || "").trim() === categoryFilter)
      .filter((d) => groupFilter === "all" || (d.groupName || "").trim() === groupFilter)
      .filter((d) =>
        statusFilter === "all" ? true : statusFilter === "active" ? d.active : !d.active
      )
      // Grupo primeiro (sem grupo vai ao fim) e, dentro dele, título — a mesma ordem do Playbook.
      .sort((a, b) => {
        const ga = (a.groupName || "").trim();
        const gb = (b.groupName || "").trim();
        if (ga !== gb) {
          if (!ga) return 1;
          if (!gb) return -1;
          return ga.localeCompare(gb, "pt-BR");
        }
        return a.title.localeCompare(b.title, "pt-BR");
      });
  }, [documents, search, categoryFilter, groupFilter, statusFilter]);

  const hasFilters =
    search.trim() !== "" ||
    categoryFilter !== "all" ||
    groupFilter !== "all" ||
    statusFilter !== "active";

  const clearFilters = () => {
    setSearch("");
    setCategoryFilter("all");
    setGroupFilter("all");
    setStatusFilter("active");
  };

  const openCreate = () => {
    setAttachments([]);
    setForm(blankForm());
  };

  const openEdit = (document: PlaybookDocumentListItem) => {
    setForm({
      id: document.id,
      title: document.title,
      summary: document.summary || "",
      category: document.category || "",
      groupName: document.groupName || "",
      videoUrl: document.videoUrl || "",
      visibilityProfileIds: document.visibilityProfileIds || [],
    });
    loadAttachments(document.id);
  };

  const loadAttachments = async (documentId: string) => {
    setAttachments([]);
    setLoadingAttachments(true);
    try {
      setAttachments(await playbookDocumentsService.getAttachments(documentId));
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao carregar os arquivos do documento."), "error");
    } finally {
      setLoadingAttachments(false);
    }
  };

  const uploadAttachment = async (file: File | null) => {
    if (!file || !form?.id) return;
    if (file.size > ATTACHMENT_MAX_BYTES) {
      showToast("O arquivo passa de 10 MB. Envie uma versão menor.", "error");
      return;
    }
    setSaving(true);
    try {
      await playbookDocumentsService.uploadAttachment(form.id, file);
      showToast("Arquivo enviado.", "success");
      await loadAttachments(form.id);
      await load();
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao enviar o arquivo."), "error");
    } finally {
      setSaving(false);
    }
  };

  const downloadAttachment = async (attachment: PlaybookDocumentAttachment) => {
    try {
      const blob = await documentLibraryService.downloadAttachment(attachment.id);
      downloadBlob(blob, attachment.fileName);
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao baixar o arquivo."), "error");
    }
  };

  const removeAttachment = async (attachment: PlaybookDocumentAttachment) => {
    if (!form?.id) return;
    setSaving(true);
    try {
      await playbookDocumentsService.removeAttachment(attachment.id);
      showToast("Arquivo excluído.", "success");
      await loadAttachments(form.id);
      await load();
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao excluir o arquivo."), "error");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!form) return;
    if (!form.title.trim()) {
      showToast("Informe o título do documento.", "error");
      return;
    }
    if (form.visibilityProfileIds.length === 0) {
      showToast(
        'Escolha pelo menos um perfil em "quem pode ver" — sem isso o documento não aparece para ninguém.',
        "error"
      );
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        summary: form.summary.trim() || null,
        category: form.category.trim() || null,
        groupName: form.groupName.trim() || null,
        videoUrl: form.videoUrl.trim() || null,
        visibilityProfileIds: form.visibilityProfileIds,
      };
      if (form.id) {
        await playbookDocumentsService.update(form.id, payload);
        setForm(null);
        showToast("Documento atualizado.", "success");
        await load();
        return;
      }

      const newId = await playbookDocumentsService.create(entityId, payload);
      // O modal continua aberto, agora em edição: o arquivo é o documento, e fechar aqui obrigaria
      // a reabrir o cadastro só para enviá-lo (o upload precisa do id, que só existe após salvar).
      setForm({ ...form, id: newId });
      setAttachments([]);
      showToast("Documento criado. Agora envie os arquivos.", "success");
      await load();
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao salvar o documento."), "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (document: PlaybookDocumentListItem) => {
    try {
      await playbookDocumentsService.setActive(document.id, !document.active);
      await load();
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao alterar a situação do documento."), "error");
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await playbookDocumentsService.remove(deleteTarget.id);
      showToast("Documento excluído.", "success");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao excluir o documento."), "error");
    } finally {
      setSaving(false);
    }
  };

  const askConfirm = (action: PendingAction) => setPendingAction(action);

  const runPendingAction = async () => {
    if (!pendingAction) return;
    await pendingAction.run();
    setPendingAction(null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box className="c4u-page-header__texts">
              <Typography variant="h4" className="c4u-page-header__title">
                Configurar biblioteca
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Manuais, políticas, modelos e vídeos que a equipe consulta na Biblioteca do Playbook. Cada
                documento aparece para os perfis escolhidos em "quem pode ver".
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
                justifyContent: "flex-end",
              }}
            >
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
              <TextField
                label="Buscar por título"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ flex: "1 1 220px", minWidth: 0 }}
                inputProps={{ maxLength: 120 }}
                InputLabelProps={{ shrink: true }}
                placeholder="ex.: Manual de acolhimento"
              />
              <FormControl size="small" sx={{ flex: "1 1 160px", minWidth: 140 }}>
                <InputLabel shrink>Área</InputLabel>
                <Select
                  label="Área"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  notched
                  displayEmpty
                >
                  <MenuItem value="all">Todas</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: "1 1 160px", minWidth: 140 }}>
                <InputLabel shrink>Grupo</InputLabel>
                <Select
                  label="Grupo"
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                  notched
                  displayEmpty
                >
                  <MenuItem value="all">Todos</MenuItem>
                  {groupOptions.map((g) => (
                    <MenuItem key={g} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: "1 1 140px", minWidth: 130 }}>
                <InputLabel shrink>Situação</InputLabel>
                <Select
                  label="Situação"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as "active" | "inactive" | "all")}
                  notched
                  displayEmpty
                >
                  <MenuItem value="active">Ativos</MenuItem>
                  <MenuItem value="inactive">Inativos</MenuItem>
                  <MenuItem value="all">Todos</MenuItem>
                </Select>
              </FormControl>
              {/* Padrão do sistema: limpar filtros é só o ícone, sem rótulo. */}
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />
                <AddButton onClick={openCreate} title="Novo documento" />
              </div>
            </div>

            <ResultCount shown={documents.length} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
              <Description sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>
                {documents.length === 0 ? "Nenhum documento cadastrado" : "Nenhum documento no filtro"}
              </Typography>
              <Typography variant="body2">
                {documents.length === 0
                  ? 'Cadastre o primeiro material da clínica em "Novo documento".'
                  : "Ajuste a busca, a área, o grupo ou a situação."}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {filtered.map((document) => (
                <Paper
                  key={document.id}
                  elevation={0}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: { xs: 1.5, sm: 2 },
                    p: 1.5,
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    opacity: document.active ? 1 : 0.65,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: colors.textPrimary,
                        wordBreak: "break-word",
                        lineHeight: 1.25,
                      }}
                    >
                      {document.title}
                    </Typography>
                    {document.summary && (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", color: colors.textSecondary, wordBreak: "break-word" }}
                      >
                        {document.summary}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                      {document.category && (
                        <Chip
                          label={document.category}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      )}
                      {document.groupName && (
                        <Chip
                          label={document.groupName}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      )}
                      {document.videoUrl && (
                        <Chip
                          icon={<OndemandVideo sx={{ fontSize: 14 }} />}
                          label="Vídeo"
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      )}
                      <Chip
                        icon={<AttachFile sx={{ fontSize: 14 }} />}
                        label={`${document.attachmentCount} ${
                          document.attachmentCount === 1 ? "arquivo" : "arquivos"
                        }`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem" }}
                      />
                      <Chip
                        icon={<Visibility sx={{ fontSize: 14 }} />}
                        label={`${document.visibilityProfileIds.length} ${
                          document.visibilityProfileIds.length === 1 ? "perfil" : "perfis"
                        }`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem" }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: { xs: "space-between", sm: "flex-end" },
                      flexShrink: 0,
                    }}
                  >
                    <Chip
                      label={document.active ? "Ativo" : "Inativo"}
                      size="small"
                      onClick={() => toggleActive(document)}
                      title={document.active ? "Clique para inativar" : "Clique para ativar"}
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        cursor: "pointer",
                        backgroundColor: document.active ? "#28a745" : "#adb5bd",
                      }}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => navigateTo("document-view", { documentId: document.id })}
                        title="Ver como a equipe vê"
                        sx={{
                          backgroundColor: "#6c757d",
                          color: "white",
                          width: 32,
                          height: 32,
                          "&:hover": { backgroundColor: "#5a6268" },
                        }}
                      >
                        <Visibility sx={{ fontSize: "1rem" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openEdit(document)}
                        title="Editar documento"
                        sx={{
                          backgroundColor: "#03B4C6",
                          color: "white",
                          width: 32,
                          height: 32,
                          "&:hover": { backgroundColor: "#029AAB" },
                        }}
                      >
                        <Edit sx={{ fontSize: "1rem" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(document)}
                        title="Excluir"
                        sx={{
                          backgroundColor: "#dc3545",
                          color: "white",
                          width: 32,
                          height: 32,
                          "&:hover": { backgroundColor: "#c82333" },
                        }}
                      >
                        <DeleteOutline sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
          </div>
        </Container>
      </Box>

      {/* Cadastro do documento (criar/editar) */}
      <Dialog
        open={form !== null}
        onClose={() => setForm(null)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
        PaperProps={{
          sx: {
            borderRadius: fullScreenDialog ? 0 : "12px",
            position: "relative",
            overflow: "hidden",
          },
        }}
      >
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "1rem 1.25rem",
          }}
        >
          <Typography
            component="h3"
            sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}
          >
            {form?.id ? "Editar documento" : "Novo documento"}
          </Typography>
          <IconButton onClick={() => setForm(null)} sx={{ color: colors.white, padding: "0.25rem" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {form && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Título"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                fullWidth
                size="small"
                required
                inputProps={{ maxLength: 120 }}
                InputLabelProps={{ shrink: true }}
                placeholder="ex.: Manual de acolhimento do paciente"
              />
              <TextField
                label="Descrição"
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                fullWidth
                size="small"
                multiline
                minRows={3}
                inputProps={{ maxLength: 500 }}
                InputLabelProps={{ shrink: true }}
                placeholder="Do que trata este material e quando a equipe deve consultá-lo."
              />
              <Autocomplete
                freeSolo
                options={CATEGORY_SUGGESTIONS}
                value={form.category}
                onChange={(_, value) => setForm({ ...form, category: value ?? "" })}
                onInputChange={(_, value) => setForm({ ...form, category: value })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Área"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ ...params.inputProps, maxLength: 60 }}
                    placeholder="ex.: Administrativo"
                  />
                )}
              />
              <Autocomplete
                freeSolo
                options={groupOptions}
                value={form.groupName}
                onChange={(_, value) => setForm({ ...form, groupName: value ?? "" })}
                onInputChange={(_, value) => setForm({ ...form, groupName: value })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Grupo"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ ...params.inputProps, maxLength: 80 }}
                    placeholder="ex.: Integração de novos terapeutas"
                    helperText="Reúne na consulta os documentos do mesmo assunto."
                  />
                )}
              />

              <FormControl size="small" fullWidth required>
                <InputLabel shrink>Quem pode ver</InputLabel>
                <Select
                  label="Quem pode ver"
                  multiple
                  notched
                  displayEmpty
                  value={form.visibilityProfileIds}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      visibilityProfileIds:
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : e.target.value,
                    })
                  }
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      <Typography sx={{ fontSize: "0.9rem", color: colors.textMuted }}>
                        Nenhum perfil selecionado
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((id) => (
                          <Chip
                            key={id}
                            label={profiles.find((p) => p.id === id)?.name ?? id}
                            size="small"
                            sx={{ height: 22, fontSize: "0.7rem" }}
                          />
                        ))}
                      </Box>
                    )
                  }
                  MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
                >
                  {profiles.map((profile) => (
                    <MenuItem key={profile.id} value={profile.id}>
                      {profile.name}
                    </MenuItem>
                  ))}
                </Select>
                <Typography
                  variant="caption"
                  sx={{
                    color: form.visibilityProfileIds.length === 0 ? colors.error : colors.textSecondary,
                    mt: 0.5,
                  }}
                >
                  Obrigatório: quem não estiver aqui não encontra o documento na Biblioteca.
                </Typography>
              </FormControl>

              <TextField
                label="Vídeo"
                value={form.videoUrl}
                onChange={(e) => setForm({ ...form, videoUrl: e.target.value })}
                fullWidth
                size="small"
                multiline
                maxRows={3}
                inputProps={{ maxLength: 1200 }}
                InputLabelProps={{ shrink: true }}
                placeholder='https://www.youtube.com/watch?v=...  ou  <iframe src="https://www.youtube.com/embed/..."></iframe>'
                helperText="Opcional. Cole o endereço do vídeo ou o código de incorporação do YouTube, Vimeo ou Loom."
              />

              <Divider />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>Arquivos</Typography>
                </Box>

                {/* Clicar ou arrastar, no mesmo componente da aba Arquivos do paciente
                    (RN-538c): a zona de soltar precisa ser visível para ser descoberta. */}
                <FileDropzone
                  onFile={(file) => uploadAttachment(file)}
                  accept={ATTACHMENT_ACCEPT}
                  disabled={!form.id || saving || attachments.length >= ATTACHMENT_MAX_COUNT}
                  disabledMessage={
                    !form.id
                      ? "Salve o documento para liberar o envio — o arquivo precisa saber a que documento pertence."
                      : attachments.length >= ATTACHMENT_MAX_COUNT
                        ? `Limite de ${ATTACHMENT_MAX_COUNT} arquivos por documento atingido.`
                        : "Enviando..."
                  }
                  hint={`Até ${ATTACHMENT_MAX_COUNT} arquivos de 10 MB (pdf, imagem, texto, planilha, documento).`}
                  compact={attachments.length > 0}
                />
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  {form.id
                    ? `Até ${ATTACHMENT_MAX_COUNT} arquivos de 10 MB (pdf, imagem, texto, planilha, documento ou apresentação).`
                    : "Salve o documento para liberar o envio — o arquivo precisa saber a que documento pertence. O cadastro continua aberto depois de salvar."}
                </Typography>

                {form.id &&
                  (loadingAttachments ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={24} sx={{ color: colors.primary }} />
                    </Box>
                  ) : attachments.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 3,
                        color: colors.textSecondary,
                        border: "1px dashed #ced4da",
                        borderRadius: "8px",
                      }}
                    >
                      <InsertDriveFile sx={{ fontSize: 36, color: "#ced4da", mb: 0.5 }} />
                      <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                        Nenhum arquivo enviado
                      </Typography>
                      <Typography variant="caption">
                        Envie o manual, a planilha ou a apresentação deste material.
                      </Typography>
                    </Box>
                  ) : (
                    attachments.map((attachment) => (
                      <Box
                        key={attachment.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          border: "1px solid #e9ecef",
                          borderRadius: "8px",
                        }}
                      >
                        <InsertDriveFile sx={{ color: colors.textSecondary, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{ color: colors.textPrimary, wordBreak: "break-word", lineHeight: 1.25 }}
                          >
                            {attachment.fileName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                            {formatFileSize(attachment.sizeBytes)}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => downloadAttachment(attachment)}
                          title="Baixar"
                          sx={{ color: colors.primary, flexShrink: 0 }}
                        >
                          <Download sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            askConfirm({
                              title: "Excluir arquivo",
                              message: `Excluir o arquivo "${attachment.fileName}"? Ele deixa de aparecer para quem consulta o documento.`,
                              confirmText: "Excluir",
                              run: () => removeAttachment(attachment),
                            })
                          }
                          title="Excluir"
                          sx={{ color: colors.error, flexShrink: 0 }}
                        >
                          <DeleteOutline sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Box>
                    ))
                  ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button
            onClick={() => setForm(null)}
            variant="outlined"
            sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}
          >
            {form?.id ? "Fechar" : "Cancelar"}
          </Button>
          <Button
            onClick={save}
            variant="contained"
            disabled={saving}
            sx={{ textTransform: "none", backgroundColor: colors.primary }}
          >
            {saving ? "Salvando..." : form?.id ? "Salvar" : "Criar documento"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir documento"
        message={`Excluir "${deleteTarget?.title ?? ""}"? Os arquivos enviados também são apagados. Para apenas tirá-lo da Biblioteca, use Inativar.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={remove}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmModal
        isOpen={pendingAction !== null}
        title={pendingAction?.title ?? ""}
        message={pendingAction?.message ?? ""}
        type="warning"
        confirmButtonText={pendingAction?.confirmText ?? "Confirmar"}
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={runPendingAction}
        onCancel={() => setPendingAction(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default PlaybookDocuments;
